import { useEffect, useMemo, useState } from 'react';

import {
    DUPLICATE_GROUP,
    DUPLICATE_SP,
    useDuplicateDashboard
} from '@/hooks/dashboard/useDuplicateDashboard';
import useSocialProfiles from '@/hooks/swr/useSocialProfiles';
import useSPGroups from '@/utils/hooks/swr/group/useSPGroups';
import useTranslation from '@/utils/hooks/translation/useTranslation';

// Utils
import { post } from '@/utils/client';
import { injectEndpointUrlParams } from '@/utils/injectEndpointUrlParams';
// Hooks
import useToggle from '@/hooks/utils/useToggle';

import PropTypes from 'prop-types';

import { DefaultAvatar as Avatar } from '@/components/base/Avatar';
import Button from '@/components/base/Button';
import Icon from '@/components/base/Icon';
import InfoBanner from '@/components/base/InfoBanner';
// Components
import Modal from '@/components/base/Modal';
import SelectGroupWidgetDashboardView from '@/components/pages/custom-dashboards/dashboard/select/SelectGroupWidgetDashboardView';
import SelectSpWidgetDashboardView from '@/components/pages/custom-dashboards/dashboard/select/SelectSPWidgetDashboardView';
import { EditNameModal } from '@/components/pages/custom-dashboards/modals/EditNameModal';
import { SuccessModal } from '@/components/pages/custom-dashboards/modals/SuccessModal';

// Lib
import { CHECK_NAME_DASHBOARD, DUPLICATE_DASHBOARD } from '@/routes/apiEndpoints';

const ReplaceLine = ({ baseElement, replacementSelector, ...restProps }) => (
    <div className="flex items-center justify-between">
        <div className="flex flex-1 items-center" data-testid={restProps['data-testid']}>
            {baseElement}
        </div>
        <Icon icon="arrow-right" className="mr-8 text-base text-icoGray-500" />
        <div className="w-[13.125rem]">{replacementSelector}</div>
    </div>
);

const ReplaceSocialProfileLine = ({ socialProfileId }) => {
    const { activatedSocialProfiles } = useSocialProfiles({
        revalidateOnMount: false,
        shouldRetryOnError: false,
        revalidateOnFocus: false,
        revalidateOnReconnect: false
    });
    const { dispatch } = useDuplicateDashboard();
    const socialProfileBase = useMemo(
        () =>
            activatedSocialProfiles?.find(
                (social_profile) => social_profile?.id === socialProfileId
            ),
        [activatedSocialProfiles, socialProfileId]
    );
    const [selectedSP, setSelectedSP] = useState(socialProfileBase);

    useEffect(() => {
        if (socialProfileBase) {
            dispatch({
                type: DUPLICATE_SP,
                payload: {
                    [socialProfileBase?.id]: selectedSP?.id
                }
            });
        }
    }, [dispatch, selectedSP, socialProfileBase]);

    if (!socialProfileBase) {
        return null;
    }

    return (
        <ReplaceLine
            baseElement={
                <>
                    <Avatar
                        fallback={socialProfileBase?.slug?.[0]}
                        size="base"
                        profileType={socialProfileBase?.social_network_type}
                        url={socialProfileBase?.external_api_data?.avatar}
                    />
                    <div className="flex flex-col pl-3.5 text-left">
                        <span className="max-w-[76px] overflow-hidden text-ellipsis font-GSemiBold text-sm text-icoBlack dark:text-white">
                            {socialProfileBase?.slug}
                        </span>
                        <span className="max-w-[76px] overflow-hidden text-ellipsis font-GMedium text-xs text-icoGray-500">
                            {socialProfileBase?.external_api_data?.name}
                        </span>
                    </div>
                </>
            }
            replacementSelector={
                <SelectSpWidgetDashboardView
                    selectedSP={selectedSP}
                    setSelectedSP={setSelectedSP}
                    networks={[selectedSP?.social_network_type]}
                    avatarSize="base"
                    baseStyle="whiteNoBorder"
                />
            }
            data-testid={`test-${socialProfileBase?.slug}`}
        />
    );
};

ReplaceSocialProfileLine.propTypes = {
    socialProfileId: PropTypes.number
};

const ReplaceGroupLine = ({ groupId }) => {
    const { activatedSpGroups } = useSPGroups({
        revalidateOnMount: false,
        revalidateOnFocus: false,
        revalidateOnReconnect: false
    });
    const { dispatch } = useDuplicateDashboard();
    const groupBase = useMemo(
        () => activatedSpGroups?.find((group) => group?.id === groupId),
        [activatedSpGroups, groupId]
    );
    const [selectedGroup, setSelectedGroup] = useState(groupBase);

    useEffect(() => {
        if (groupBase) {
            dispatch({
                type: DUPLICATE_GROUP,
                payload: {
                    [groupBase?.id]: selectedGroup?.id
                }
            });
        }
    }, [dispatch, selectedGroup, groupBase]);

    if (!groupBase) {
        return null;
    }

    return (
        <ReplaceLine
            baseElement={
                <>
                    <Avatar
                        profileType={null}
                        size="base"
                        radius="rounded-m"
                        url={groupBase?.image ?? null}
                        fallback={groupBase?.name?.substring(0, 2)}
                        customColorClass={groupBase?.fallback_style}
                    />
                    <div className="pl-3.5 text-left">
                        <span className="max-w-[76px] overflow-hidden text-ellipsis font-GSemiBold text-sm text-icoBlack dark:text-white">
                            {groupBase?.name}
                        </span>
                    </div>
                </>
            }
            replacementSelector={
                <SelectGroupWidgetDashboardView
                    selectedGroup={selectedGroup}
                    setSelectedGroup={setSelectedGroup}
                    avatarSize="base"
                    sizeStyle="sm"
                    baseStyle="whiteNoBorder"
                />
            }
            data-testid={`test-group-${groupBase?.id}`}
        />
    );
};

ReplaceGroupLine.propTypes = {
    groupId: PropTypes.number
};

const DuplicateModals = ({ dashboard, isOpen, toggle, onSuccess }) => {
    const { t } = useTranslation('commons');
    const [status, setStatus] = useState({
        isLoading: false,
        isSuccess: false,
        errorMessage: null,
        newDashboardId: null
    });
    const { duplicateDashboardData } = useDuplicateDashboard();

    const { isOpen: isOpenDuplicateSPModal, toggle: toggleDuplicateSPModal } = useToggle({
        initial: false
    });
    const { isOpen: isOpenSuccessSPModal, toggle: toggleSuccessSPModal } = useToggle({
        initial: false
    });

    /**
     * Duplicate dashboard with new name
     * @returns {Promise<void>}
     */
    const duplicateDashboard = async () => {
        setStatus((prevState) => ({
            ...prevState,
            isLoading: true,
            errorMessage: null,
            newDashboardId: null
        }));

        const formatReplacementData = (replacementData) =>
            Object.keys(replacementData).map((sp) => ({
                from: sp,
                to: replacementData[sp]
            }));

        try {
            await post(
                injectEndpointUrlParams(DUPLICATE_DASHBOARD, { dashboardId: dashboard.id }),
                {
                    social_profiles: duplicateDashboardData.social_profiles
                        ? formatReplacementData(duplicateDashboardData.social_profiles)
                        : [],
                    groups: duplicateDashboardData.groups
                        ? formatReplacementData(duplicateDashboardData.groups)
                        : [],
                    name: duplicateDashboardData.name ?? ''
                }
            ).then(({ data }) => {
                setStatus((prevState) => ({
                    ...prevState,
                    isSuccess: true,
                    isLoading: false,
                    newDashboardId: data?.id
                }));
                toggleDuplicateSPModal();
                toggleSuccessSPModal();
            });
        } catch (e) {
            setStatus((prevState) => ({
                ...prevState,
                isSuccess: true,
                errorMessage: t('errors:error_occured_submit_data'),
                isLoading: false
            }));
        }
    };

    return (
        <>
            <EditNameModal
                isOpen={isOpen}
                toggle={toggle}
                onConfirm={async (name) => {
                    await post(CHECK_NAME_DASHBOARD, {
                        name
                    });
                    toggleDuplicateSPModal();
                    toggle();
                }}
                title={t('dashboard:new_dashboard_name')}
            />
            <Modal
                open={isOpenDuplicateSPModal}
                onDismiss={toggleDuplicateSPModal}
                scrollInside
                scrollInsideWrapperClass="flex flex-col max-h-[480px] scrollbar">
                <Modal.Body>
                    <Modal.Title>
                        {t('dashboard:duplicate_dashboard_title')}
                        <p className="pt-5 font-GMedium text-base text-icoGray-500">
                            {t('dashboard:duplicate_dashboard_notice')}
                        </p>
                    </Modal.Title>
                    <Modal.Content className="pt-8 text-center text-sm">
                        {status.errorMessage ? (
                            <InfoBanner
                                className="mb-4"
                                title={t('error')}
                                message={status.errorMessage}
                                onClose={() =>
                                    setStatus((prevState) => ({
                                        ...prevState,
                                        errorMessage: null
                                    }))
                                }
                            />
                        ) : null}
                        <div className="space-y-3 rounded-md bg-icoGray-100 p-8 dark:bg-icoDarkMode-anthracite">
                            {dashboard.data?.social_profiles.map((social_profile_id) => (
                                <ReplaceSocialProfileLine
                                    key={social_profile_id}
                                    socialProfileId={social_profile_id}
                                />
                            ))}
                            {dashboard.data?.groups?.map((group_id) => (
                                <ReplaceGroupLine key={group_id} groupId={group_id} />
                            ))}
                        </div>
                    </Modal.Content>
                </Modal.Body>
                <Modal.Footer>
                    <button
                        type="button"
                        onClick={() => {
                            toggle();
                            toggleDuplicateSPModal();
                        }}
                        className="font-GSemiBold text-xs text-icoGray-500 underline dark:text-icoDarkMode-wolf">
                        {t('back')}
                    </button>
                    <Button onClick={duplicateDashboard} isLoading={status.isLoading}>
                        {t('dashboard:create_dashboard')}
                    </Button>
                </Modal.Footer>
            </Modal>
            <SuccessModal
                notice={t('dashboard:duplicate_dashboard_confirmation_success')}
                isOpen={isOpenSuccessSPModal}
                toggle={() => {
                    if (onSuccess) {
                        onSuccess();
                    }

                    toggleSuccessSPModal();
                }}
                dashboardId={status.newDashboardId}
                buttonText={t('dashboard:go_to_dashboard')}
            />
        </>
    );
};

DuplicateModals.propTypes = {
    dashboard: PropTypes.object,
    isOpen: PropTypes.bool,
    toggle: PropTypes.func,
    onConfirm: PropTypes.func,
    status: PropTypes.object,
    setStatus: PropTypes.func
};

export default DuplicateModals;
